import {CodeAndExchange} from "../../../../../instruments/types/CodeAndExchange";
import {OrderLifetime, OrderSide} from "../../../../../orders/models/OrderRecord";
import {EquityOrderType} from "./EquityWizardData";
import {OrderAdvice} from "../../place-order-wizard/models/OrderAdvice";
import {QuoteViewModel} from "../../../../../quote/models/QuoteViewModel";
import {AccountBriefRecord} from "../../../../../account/types/AccountBriefRecord";
import {PricingInstruction} from "../../../../../orders/models/PricingInstruction";
import {PriceStrategy} from "../../../../../components/inputs/price/input/PriceStrategy";
import {ChildOrder} from "../../place-order-wizard/models/ChildOrder";

export interface EquityWizardDataChange {
    readonly type: ChangeType;
}

export enum ChangeType {
    SECURITY,
    NAME,
    SIDE,
    QUANTITY,
    LIMIT,
    TOTAL_VALUE,
    LIMIT_TO_MID,
    LIFETIME,
    EXPIRY,
    INSTRUCTION,
    TYPE,
    ADVICE,
    QUOTE,
    PORTFOLIO,
    TOGGLE_STOP_LOSS,
    STOP_LOSS_LIMIT,
    PARENT_ORDER,
    ADD_CHILD_ORDER,
    REMOVE_CHILD_ORDER,
    UPDATE_CHILD_ORDER
}

export interface EquityWizardDataSecurityChange extends EquityWizardDataChange {
    readonly type: ChangeType.SECURITY;
    readonly security: CodeAndExchange;
    readonly strategy: PriceStrategy;
}

export function securityChange(security: CodeAndExchange, strategy: PriceStrategy): EquityWizardDataSecurityChange {
    return {
        type: ChangeType.SECURITY,
        security,
        strategy
    };
}

export function nameChange(name: string): EquityWizardNameChange {
    return {
        type: ChangeType.NAME,
        name
    };
}

export function isSecurityChange(change: EquityWizardDataChange): change is EquityWizardDataSecurityChange {
    return change.type === ChangeType.SECURITY;
}

export function isNameChange(change: EquityWizardDataChange): change is EquityWizardNameChange {
    return change.type === ChangeType.NAME;
}

export interface EquityWizardDataSideChange extends EquityWizardDataChange {
    readonly type: ChangeType.SIDE;
    readonly side: OrderSide;
}

export interface EquityWizardNameChange extends EquityWizardDataChange {
    readonly type: ChangeType.NAME;
    readonly name: string;
}

export function sideChange(side: OrderSide): EquityWizardDataSideChange {
    return {
        type: ChangeType.SIDE,
        side
    };
}

export function isSideChange(change: EquityWizardDataChange): change is EquityWizardDataSideChange {
    return change.type === ChangeType.SIDE;
}

export interface EquityWizardDataQuantityChange extends EquityWizardDataChange {
    readonly type: ChangeType.QUANTITY;
    readonly quantity: number;
}

export function quantityChange(quantity: number): EquityWizardDataQuantityChange {
    return {
        type: ChangeType.QUANTITY,
        quantity
    };
}

export function isQuantityChange(change: EquityWizardDataChange): change is EquityWizardDataQuantityChange {
    return change.type === ChangeType.QUANTITY;
}

export interface EquityWizardDataLimitChange extends EquityWizardDataChange {
    readonly type: ChangeType.LIMIT;
    readonly limit: number;
}

export function limitChange(limit: number): EquityWizardDataLimitChange {
    return {
        type: ChangeType.LIMIT,
        limit
    };
}

export function isLimitChange(change: EquityWizardDataChange): change is EquityWizardDataLimitChange {
    return change.type === ChangeType.LIMIT;
}

export interface EquityWizardDataTotalValueChange extends EquityWizardDataChange {
    readonly type: ChangeType.TOTAL_VALUE;
    readonly totalValue: number;
}

export function totalValueChange(totalValue: number): EquityWizardDataTotalValueChange {
    return {
        type: ChangeType.TOTAL_VALUE,
        totalValue
    };
}

export function isTotalValueChange(change: EquityWizardDataChange): change is EquityWizardDataTotalValueChange {
    return change.type === ChangeType.TOTAL_VALUE;
}

export interface EquityWizardDataLimitToMidChange extends EquityWizardDataChange {
    readonly type: ChangeType.LIMIT_TO_MID;
}

export function limitToMidChange(): EquityWizardDataLimitToMidChange {
    return {
        type: ChangeType.LIMIT_TO_MID
    };
}

export function isLimitToMidChange(change: EquityWizardDataChange): change is EquityWizardDataLimitToMidChange {
    return change.type === ChangeType.LIMIT_TO_MID;
}

export interface EquityWizardDataLifetimeChange extends EquityWizardDataChange {
    readonly type: ChangeType.LIFETIME;
    readonly lifetime: OrderLifetime;
}

export function lifetimeChange(lifetime: OrderLifetime): EquityWizardDataLifetimeChange {
    return {
        type: ChangeType.LIFETIME,
        lifetime
    };
}

export function isLifetimeChange(change: EquityWizardDataChange): change is EquityWizardDataLifetimeChange {
    return change.type === ChangeType.LIFETIME;
}

export interface EquityWizardIsParentOrderChange extends EquityWizardDataChange {
    readonly type: ChangeType.PARENT_ORDER;
    readonly isParentOrder: boolean;
}

export function parentOrderChange(isParentOrder: boolean): EquityWizardIsParentOrderChange {
    return {
        type: ChangeType.PARENT_ORDER,
        isParentOrder
    };
}

export function isParentOrderChange(change: EquityWizardDataChange): change is EquityWizardIsParentOrderChange {
    return change.type === ChangeType.PARENT_ORDER;
}

export interface EquityWizardAddChildOrderChange extends EquityWizardDataChange {
    readonly type: ChangeType.ADD_CHILD_ORDER;
    readonly childOrder: ChildOrder;
}

export function addChildOrder(childOrder: ChildOrder): EquityWizardAddChildOrderChange {
    return {
        type: ChangeType.ADD_CHILD_ORDER,
        childOrder
    };
}

export function addChildOrderChange(change: EquityWizardDataChange): change is EquityWizardAddChildOrderChange {
    return change.type === ChangeType.ADD_CHILD_ORDER;
}

export interface EquityWizardRemoveChildOrderChange extends EquityWizardDataChange {
    readonly type: ChangeType.REMOVE_CHILD_ORDER;
    readonly index: number;
}

export function removeChildOrder(index: number): EquityWizardRemoveChildOrderChange {
    return {
        type: ChangeType.REMOVE_CHILD_ORDER,
        index
    };
}

export function removeChildOrderChange(change: EquityWizardDataChange): change is EquityWizardRemoveChildOrderChange {
    return change.type === ChangeType.REMOVE_CHILD_ORDER;
}

export interface EquityWizardUpdateChildOrderChange extends EquityWizardDataChange {
    readonly type: ChangeType.UPDATE_CHILD_ORDER;
    readonly index: number;
    readonly childOrder: ChildOrder;
}

export function updateChildOrder(index: number, childOrder: ChildOrder): EquityWizardUpdateChildOrderChange {
    return {
        type: ChangeType.UPDATE_CHILD_ORDER,
        index,
        childOrder
    };
}

export function updateChildOrderChange(change: EquityWizardDataChange): change is EquityWizardUpdateChildOrderChange {
    return change.type === ChangeType.UPDATE_CHILD_ORDER;
}

export interface EquityWizardDataExpiryChange extends EquityWizardDataChange {
    readonly type: ChangeType.EXPIRY;
    readonly expiry: Date | null;
}

export function expiryChange(expiry: Date | null): EquityWizardDataExpiryChange {
    return {
        type: ChangeType.EXPIRY,
        expiry
    };
}

export function isExpiryChange(change: EquityWizardDataChange): change is EquityWizardDataExpiryChange {
    return change.type === ChangeType.EXPIRY;
}

export interface EquityWizardDataInstructionChange extends EquityWizardDataChange {
    readonly type: ChangeType.INSTRUCTION;
    readonly instruction: PricingInstruction;
}

export function instructionChange(instruction: PricingInstruction): EquityWizardDataInstructionChange {
    return {
        type: ChangeType.INSTRUCTION,
        instruction
    };
}

export function isInstructionChange(change: EquityWizardDataChange): change is EquityWizardDataInstructionChange {
    return change.type === ChangeType.INSTRUCTION;
}

export interface EquityWizardDataTypeChange extends EquityWizardDataChange {
    readonly type: ChangeType.TYPE;
    readonly orderType: EquityOrderType;
}

export function orderTypeChange(orderType: EquityOrderType): EquityWizardDataTypeChange {
    return {
        type: ChangeType.TYPE,
        orderType
    };
}

export function isOrderTypeChange(change: EquityWizardDataChange): change is EquityWizardDataTypeChange {
    return change.type === ChangeType.TYPE;
}

export interface EquityWizardDataAdviceChange extends EquityWizardDataChange {
    readonly type: ChangeType.ADVICE;
    readonly advice: OrderAdvice | null;
}

export function adviceChange(advice: OrderAdvice | null): EquityWizardDataAdviceChange {
    return {
        type: ChangeType.ADVICE,
        advice
    };
}

export function isAdviceChange(change: EquityWizardDataChange): change is EquityWizardDataAdviceChange {
    return change.type === ChangeType.ADVICE;
}

export interface EquityWizardDataQuoteChange extends EquityWizardDataChange {
    readonly type: ChangeType.QUOTE;
    readonly quote: QuoteViewModel;
}

export function quoteChange(quote: QuoteViewModel): EquityWizardDataQuoteChange {
    return {
        type: ChangeType.QUOTE,
        quote
    };
}

export function isQuoteChange(change: EquityWizardDataChange): change is EquityWizardDataQuoteChange {
    return change.type === ChangeType.QUOTE;
}

export interface EquityWizardDataPortfolioChange extends EquityWizardDataChange {
    readonly type: ChangeType.PORTFOLIO;
    readonly portfolio: AccountBriefRecord;
}

export function portfolioChange(portfolio: AccountBriefRecord): EquityWizardDataPortfolioChange {
    return {
        type: ChangeType.PORTFOLIO,
        portfolio
    };
}

export function isPortfolioChange(change: EquityWizardDataChange): change is EquityWizardDataPortfolioChange {
    return change.type === ChangeType.PORTFOLIO;
}

export interface EquityWizardDataStopLossToggleChange extends EquityWizardDataChange {
    readonly type: ChangeType.TOGGLE_STOP_LOSS;
}

export function stopLossToggleChange(): EquityWizardDataStopLossToggleChange {
    return {
        type: ChangeType.TOGGLE_STOP_LOSS
    };
}

export function isStopLossToggleChange(change: EquityWizardDataChange): change is EquityWizardDataStopLossToggleChange {
    return change.type === ChangeType.TOGGLE_STOP_LOSS;
}

export interface EquityWizardDataStopLossLimitChange extends EquityWizardDataChange {
    readonly type: ChangeType.STOP_LOSS_LIMIT;
    readonly stopLossLimit: number | null;
}

export function stopLossLimitChange(limit: number): EquityWizardDataStopLossLimitChange {
    return {
        type: ChangeType.STOP_LOSS_LIMIT,
        stopLossLimit: limit
    };
}

export function isStopLossLimitChange(change: EquityWizardDataChange): change is EquityWizardDataStopLossLimitChange {
    return change.type === ChangeType.STOP_LOSS_LIMIT;
}
